"use client";

import React from "react";
import { Users, Briefcase, TrendingUp, ArrowRight, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const networkBenefits = [{
  icon: Users,
  title: "Expand Your Reach",
  description: "Connect with hundreds of local professionals and potential clients in your area."
}, {
  icon: Briefcase,
  title: "Exclusive Opportunities",
  description: "Access member-only business deals, partnerships, and referral networks."
}, {
  icon: TrendingUp,
  title: "Grow Together",
  description: "Participate in specialized workshops, seminars, and collaborative events."
}];
export default function FormHeader() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: cardRef,
    isVisible: isCardVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="form-header" className="relative w-full bg-background py-16 md:py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/3 w-[400px] h-[400px] bg-secondary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {}
          <div ref={textRef} className={`flex flex-col items-start space-y-8 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
              <span className="flex h-2 w-2 rounded-full bg-primary mr-2"></span>
              Membership Application
            </div>
            
            <div className="space-y-4">
              <Link to="/">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/wt_birkalla_logo_gold.png" className="h-20 w-auto object-contain mb-8" alt="Birkalla Logo" loading="lazy" />
              </Link>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default text-foreground leading-tight tracking-tight">
                Join the Birks <br className="hidden md:block" />
                <span className="text-primary">Business Network</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-[600px] leading-relaxed">
                Connect with local businesses, share opportunities, and grow together. Take the first step towards expanding your professional footprint.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <Button size="lg" className="h-12 px-8 text-base" asChild>
                <Link to="#application-form">
                  Apply Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="h-12 px-8 text-base" asChild>
                <Link to="/BusinessDirectoryPage">
                  View Directory
                </Link>
              </Button>
            </div>

            <div className="flex items-center gap-4 text-sm text-muted-foreground pt-4">
              <div className="flex items-center gap-1">
                <CircleCheck className="h-4 w-4 text-primary" />
                <span>Fast approval</span>
              </div>
              <div className="flex items-center gap-1">
                <CircleCheck className="h-4 w-4 text-primary" />
                <span>No hidden fees</span>
              </div>
            </div>
          </div>

          {}
          <div ref={cardRef} className={`transition-all duration-1000 delay-200 ease-out ${isCardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Card className="border-border/50 shadow-2xl shadow-primary/5 bg-card/50 backdrop-blur-sm">
              <CardHeader className="pb-8">
                <CardTitle className="text-2xl font-default text-foreground">
                  Why Join Our Network?
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-8">
                {networkBenefits.map((benefit, index) => <div key={index} data-index={index} className="flex gap-5 items-start">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <benefit.icon className="h-6 w-6" />
                    </div>
                    <div className="space-y-1.5">
                      <h3 className="text-lg font-semibold font-default text-foreground">
                        {benefit.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </div>)}
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}